import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { StepUpSpinner } from "../../Components/Loader";
import {
  Box, Typography, TextField, IconButton, Avatar, Chip, Button,
  alpha, useTheme, CircularProgress, InputAdornment, Badge,
  Skeleton, Checkbox, Divider, useMediaQuery,
} from "@mui/material";
import {
  useGetChatConversationsQuery, useGetChatMessagesQuery, useSendChatMessageMutation,
  useGetChatContactsQuery, useCreateChatConversationMutation, useMarkChatAsReadMutation,
} from "../../Api/attoDeskApi";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";

import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

dayjs.extend(relativeTime);
dayjs.extend(calendar);

type Filter = "all" | "unread" | "parents" | "staff";

const formatDayLabel = (dateStr: string) => {
  const d = dayjs(dateStr);
  if (d.isSame(dayjs(), "day")) return "Today";
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return d.format("MMMM D, YYYY");
};

const ChatPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedConvId, setSelectedConvId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [showNewChat, setShowNewChat] = useState(false);

  const { data: convData, isLoading: convLoading } = useGetChatConversationsQuery(undefined, { pollingInterval: 6000 });
  const conversations: any[] = useMemo(() => Array.isArray(convData?.data) ? convData!.data as any[] : [], [convData]);
  const totalUnread = conversations.reduce((s: number, c: any) => s + (c.unread_count || 0), 0);

  const filtered = useMemo(() => {
    let list = conversations;
    if (search) { const q = search.toLowerCase(); list = list.filter((c: any) => (c.title || "").toLowerCase().includes(q)); }
    if (filter === "unread") list = list.filter((c: any) => (c.unread_count || 0) > 0);
    if (filter === "parents") list = list.filter((c: any) => c.participants?.some((p: any) => p.user_type === "parent"));
    if (filter === "staff") list = list.filter((c: any) => c.participants?.every((p: any) => p.user_type === "employee"));
    return list;
  }, [conversations, search, filter]);

  const selectedConv = conversations.find((c: any) => c.id === selectedConvId) || null;
  const filters: { key: Filter; label: string }[] = [
    { key: "all", label: "All" }, { key: "unread", label: `Unread${totalUnread ? ` (${totalUnread})` : ""}` },
    { key: "parents", label: "Parents" }, { key: "staff", label: "Staff" },
  ];

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 100px)", borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
      {/* LEFT */}
      <Box sx={{ width: isMobile && selectedConvId ? 0 : isMobile ? "100%" : 360, borderRight: "1px solid rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden", transition: "width 0.2s" }}>
        <Box sx={{ px: 2, py: 1.75, borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Messages</Typography>
            {totalUnread > 0 && <Chip label={totalUnread} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: theme.palette.primary.main, color: "#fff" }} />}
          </Box>
          <IconButton size="small" onClick={() => setShowNewChat(!showNewChat)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
            <AddCommentOutlinedIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <TextField size="small" fullWidth placeholder="Search conversations..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>, sx: { fontSize: 12.5, borderRadius: "12px", bgcolor: "rgba(0,0,0,0.02)", height: 36 } }} />
        </Box>

        <Box sx={{ px: 1.5, py: 0.75, display: "flex", gap: 0.5, overflowX: "auto", "&::-webkit-scrollbar": { display: "none" } }}>
          {filters.map((f) => (
            <Chip key={f.key} label={f.label} size="small" onClick={() => setFilter(f.key)} sx={{
              height: 26, fontSize: 11, fontWeight: filter === f.key ? 700 : 500, flexShrink: 0, cursor: "pointer",
              bgcolor: filter === f.key ? alpha(theme.palette.primary.main, 0.1) : "rgba(0,0,0,0.03)",
              color: filter === f.key ? theme.palette.primary.main : "text.secondary",
              border: filter === f.key ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : "1px solid transparent",
            }} />
          ))}
        </Box>
        <Divider sx={{ mx: 1.5 }} />

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {convLoading ? (
            <Box sx={{ px: 1.5 }}>{[1,2,3,4,5].map((i) => <Box key={i} sx={{ display: "flex", gap: 1.25, py: 1.25 }}><Skeleton variant="circular" width={44} height={44} /><Box sx={{ flex: 1 }}><Skeleton width="60%" height={16} /><Skeleton width="80%" height={14} sx={{ mt: 0.5 }} /></Box></Box>)}</Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: "rgba(0,0,0,0.08)", mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>No conversations</Typography>
              <Button size="small" variant="contained" onClick={() => setShowNewChat(true)} sx={{ mt: 1.5, textTransform: "none", fontSize: 12, boxShadow: "none", borderRadius: "10px" }}>New Conversation</Button>
            </Box>
          ) : filtered.map((conv: any) => {
            const hasUnread = (conv.unread_count || 0) > 0;
            const isActive = selectedConvId === conv.id;
            return (
              <Box key={conv.id} onClick={() => { setSelectedConvId(conv.id); setShowNewChat(false); }}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.35, cursor: "pointer",
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : hasUnread ? alpha(theme.palette.primary.main, 0.02) : "transparent",
                  borderLeft: isActive ? `3px solid ${theme.palette.primary.main}` : hasUnread ? `3px solid ${alpha(theme.palette.primary.main, 0.4)}` : "3px solid transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  borderBottom: "1px solid rgba(0,0,0,0.04)", transition: "all 0.1s",
                }}>
                <Avatar sx={{ width: 44, height: 44, fontSize: 15, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.07), color: theme.palette.primary.main }}>
                  {conv.type === "group" ? <GroupsIcon sx={{ fontSize: 20 }} /> : (conv.title || "C").charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: hasUnread ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{conv.title}</Typography>
                    {conv.latest_message && <Typography sx={{ fontSize: 10, color: hasUnread ? theme.palette.primary.main : "text.disabled", fontWeight: hasUnread ? 700 : 400, flexShrink: 0, ml: 1 }}>{dayjs(conv.latest_message.created_at).fromNow(true)}</Typography>}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.2 }}>
                    <Typography sx={{ flex: 1, fontSize: 12, color: hasUnread ? "text.secondary" : "text.disabled", fontWeight: hasUnread ? 500 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {conv.latest_message?.body || "No messages yet"}
                    </Typography>
                    {hasUnread && <Chip label={conv.unread_count} size="small" sx={{ height: 18, minWidth: 18, fontSize: 10, fontWeight: 700, bgcolor: theme.palette.primary.main, color: "#fff", "& .MuiChip-label": { px: 0.5 } }} />}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* RIGHT */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {showNewChat ? (
          <NewChatInline onCreated={(id) => { setSelectedConvId(id); setShowNewChat(false); }} onClose={() => setShowNewChat(false)} />
        ) : selectedConvId ? (
          <FullThreadPanel conversationId={selectedConvId} conversation={selectedConv} onBack={isMobile ? () => setSelectedConvId(null) : undefined} />
        ) : (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.06)", mb: 2 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.secondary" }}>Select a conversation</Typography>
            <Typography sx={{ fontSize: 13, color: "text.disabled", mt: 0.5 }}>Choose from the list or start a new one</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Full Thread Panel (reuses same bubble style as widget) ──

const FullThreadPanel = ({ conversationId, conversation, onBack }: { conversationId: number; conversation: any; onBack?: () => void }) => {
  const theme = useTheme();
  const [text, setText] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: msgData, isLoading } = useGetChatMessagesQuery({ conversation_id: conversationId }, { pollingInterval: 4000 });
  const [sendMsg, { isLoading: sending }] = useSendChatMessageMutation();
  const [markRead] = useMarkChatAsReadMutation();
  const messages: any[] = useMemo(() => Array.isArray(msgData?.data) ? msgData!.data as any[] : [], [msgData]);

  useEffect(() => { markRead({ conversation_id: conversationId }); }, [conversationId]);
  useEffect(() => { setTimeout(() => endRef.current?.scrollIntoView({ behavior: "auto" }), 80); }, [conversationId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const handleSend = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setText("");
    try { await sendMsg({ conversation_id: conversationId, body }).unwrap(); } catch {}
  };

  const grouped = useMemo(() => {
    const g: { date: string; msgs: any[] }[] = [];
    let last = "";
    messages.forEach((m: any) => {
      const d = dayjs(m.created_at).format("YYYY-MM-DD");
      if (d !== last) { g.push({ date: d, msgs: [m] }); last = d; } else { g[g.length - 1].msgs.push(m); }
    });
    return g;
  }, [messages]);

  return (
    <>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 1.5 }}>
        {onBack && <IconButton size="small" onClick={onBack}><ArrowBackIcon sx={{ fontSize: 20 }} /></IconButton>}
        <Avatar sx={{ width: 38, height: 38, fontSize: 14, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}>
          {(conversation?.title || "C").charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{conversation?.title || "Conversation"}</Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{conversation?.participants?.length || 0} participants</Typography>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 1.5, display: "flex", flexDirection: "column", bgcolor: "rgba(0,0,0,0.012)" }}>
        {isLoading ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><StepUpSpinner size="small" /></Box> : messages.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}><Typography sx={{ fontSize: 13, color: "text.secondary" }}>No messages yet — say hello!</Typography></Box>
        ) : grouped.map((g) => (
          <React.Fragment key={g.date}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em" }}>{formatDayLabel(g.date)}</Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
            </Box>
            {g.msgs.map((msg: any, i: number) => {
              const isMine = msg.is_mine;
              const showSender = !isMine && (i === 0 || g.msgs[i - 1]?.sender_id !== msg.sender_id);
              return (
                <Box key={msg.id} sx={{ display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start", mt: showSender ? 1.5 : 0.25 }}>
                  {showSender && <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "text.secondary", mb: 0.2, ml: 0.5 }}>{msg.sender_name}</Typography>}
                  <Box sx={{
                    maxWidth: "65%", px: 1.75, py: 1, borderRadius: isMine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    bgcolor: isMine ? theme.palette.primary.main : "#f0f1f4", color: isMine ? "#fff" : "text.primary",
                    boxShadow: isMine ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  }}>
                    <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{msg.body}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.3, mt: 0.2 }}>
                      <Typography sx={{ fontSize: 9.5, opacity: 0.5 }}>{dayjs(msg.created_at).format("h:mm A")}</Typography>
                      {isMine && <DoneAllIcon sx={{ fontSize: 13, opacity: 0.5 }} />}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
        <div ref={endRef} />
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", gap: 1, alignItems: "flex-end" }}>
        <TextField fullWidth multiline maxRows={4} size="small" placeholder="Type a message..."
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          InputProps={{ sx: { fontSize: 13.5, borderRadius: "14px", bgcolor: "rgba(0,0,0,0.02)" } }} />
        <IconButton onClick={handleSend} disabled={!text.trim() || sending}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
            color: "#fff", width: 42, height: 42, "&:hover": { background: theme.palette.primary.dark },
            "&:disabled": { background: "rgba(0,0,0,0.06)", color: "text.disabled" },
          }}>
          {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>
    </>
  );
};

// ── Inline New Chat ──

const NewChatInline = ({ onCreated, onClose }: { onCreated: (id: number) => void; onClose: () => void }) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [msg, setMsg] = useState("");

  const { data: contactData, isLoading } = useGetChatContactsQuery();
  const [createConv, { isLoading: creating }] = useCreateChatConversationMutation();

  const contacts: any[] = useMemo(() => Array.isArray(contactData?.data) ? contactData!.data as any[] : [], [contactData]);
  const filtered = useMemo(() => { if (!search) return contacts; const q = search.toLowerCase(); return contacts.filter((c: any) => (c.name || "").toLowerCase().includes(q)); }, [contacts, search]);

  const handleCreate = async () => {
    if (selected.length === 0) return;
    try { const r = await createConv({ type: selected.length === 1 ? "direct" : "group", participant_ids: selected, initial_message: msg || undefined }).unwrap(); if (r.status && r.data) onCreated((r.data as any).id); } catch {}
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>New Conversation</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <Box sx={{ px: 2, py: 1 }}>
        <TextField size="small" fullWidth placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>, sx: { fontSize: 13, borderRadius: "12px" } }} />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><StepUpSpinner size="small" /></Box> :
          filtered.map((c: any) => (
            <Box key={c.id} onClick={() => setSelected(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1, cursor: "pointer", bgcolor: selected.includes(c.id) ? alpha(theme.palette.primary.main, 0.04) : "transparent", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) }, borderBottom: "1px solid rgba(0,0,0,0.03)" }}>
              <Avatar sx={{ width: 38, height: 38, fontSize: 14, fontWeight: 700, bgcolor: alpha(c.user_type === "parent" ? "#f59e0b" : theme.palette.primary.main, 0.08), color: c.user_type === "parent" ? "#f59e0b" : theme.palette.primary.main }}>{(c.name || "U").charAt(0).toUpperCase()}</Avatar>
              <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 13, fontWeight: 500 }}>{c.name}</Typography><Typography sx={{ fontSize: 10.5, color: "text.disabled", textTransform: "capitalize" }}>{c.user_type}</Typography></Box>
              <Checkbox checked={selected.includes(c.id)} size="small" sx={{ p: 0 }} />
            </Box>
          ))}
      </Box>
      {selected.length > 0 && (
        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <TextField size="small" fullWidth placeholder="Write a first message (optional)..." value={msg} onChange={(e) => setMsg(e.target.value)} sx={{ mb: 1 }}
            InputProps={{ sx: { fontSize: 13, borderRadius: "12px" } }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{selected.length} selected</Typography>
            <Button variant="contained" size="small" onClick={handleCreate} disabled={creating}
              sx={{ textTransform: "none", fontSize: 12, boxShadow: "none", borderRadius: "10px", px: 3 }}>
              {creating ? <CircularProgress size={14} color="inherit" /> : "Start Chat"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChatPage;
